import {
  getCampaignPeriodDateErrors,
  type CampaignDraftCreatorItem,
  type CampaignDraftPostItem,
} from '@/entities/campaign';

import { getCreatorRowError, getPostRowError } from '../../hooks';
import type { CampaignEditorSubmitPayload } from '../campaign-editor-types';

export type CampaignEditorValidationIssue =
  | { section: 'campaign-info'; reason: 'incomplete' | 'invalid' }
  | { section: 'creators'; reason: 'invalid-row' }
  | { section: 'post-urls'; reason: 'invalid-row' }
  | { section: 'tracking-items'; reason: 'empty' | 'limit-exceeded' };

export function getCampaignEditorCreatorIssues(creatorItems: CampaignDraftCreatorItem[]) {
  return creatorItems
    .filter((item) => item.sourceValue.trim().length > 0)
    .map((item) => ({
      rowId: item.id,
      message: getCreatorRowError(item),
    }))
    .filter((item): item is { rowId: string; message: string } => Boolean(item.message));
}

export function getCampaignEditorPostIssues(postItems: CampaignDraftPostItem[]) {
  return postItems
    .filter((item) => item.postUrl.trim().length > 0)
    .map((item) => ({
      rowId: item.id,
      message: getPostRowError(item),
    }))
    .filter((item): item is { rowId: string; message: string } => Boolean(item.message));
}

export function getCampaignEditorTrackingIssues({
  campaignItemSlotLimit,
  totalTrackedItems,
}: {
  campaignItemSlotLimit: number;
  totalTrackedItems: number;
}): CampaignEditorValidationIssue[] {
  const issues: CampaignEditorValidationIssue[] = [];

  if (totalTrackedItems === 0) {
    issues.push({ section: 'tracking-items', reason: 'empty' });
  }

  if (totalTrackedItems > campaignItemSlotLimit) {
    issues.push({ section: 'tracking-items', reason: 'limit-exceeded' });
  }

  return issues;
}

export function getCampaignEditorSubmitValidationIssues({
  campaignItemSlotLimit,
  payload,
  startDateEditable,
  workspaceTimezone,
}: {
  campaignItemSlotLimit: number;
  payload: CampaignEditorSubmitPayload;
  startDateEditable: boolean;
  workspaceTimezone?: string;
}): CampaignEditorValidationIssue[] {
  const issues: CampaignEditorValidationIssue[] = [];

  if (!payload.campaignName.trim()) {
    issues.push({ section: 'campaign-info', reason: 'incomplete' });
  }

  const periodErrors = getCampaignPeriodDateErrors({
    campaignEndDate: payload.campaignEndDate,
    campaignStartDate: payload.campaignStartDate,
    hasEndDate: payload.hasEndDate,
    workspaceTimezone,
  });

  if (startDateEditable && periodErrors.startDate) {
    issues.push({
      section: 'campaign-info',
      reason: periodErrors.startDate === 'required' ? 'incomplete' : 'invalid',
    });
  }

  if (periodErrors.endDate) {
    issues.push({
      section: 'campaign-info',
      reason: periodErrors.endDate === 'required' ? 'incomplete' : 'invalid',
    });
  }

  issues.push(
    ...getCampaignEditorTrackingIssues({
      campaignItemSlotLimit,
      totalTrackedItems: payload.creatorItems.length + payload.postItems.length,
    }),
  );

  if (getCampaignEditorCreatorIssues(payload.creatorItems).length > 0) {
    issues.push({ section: 'creators', reason: 'invalid-row' });
  }

  if (getCampaignEditorPostIssues(payload.postItems).length > 0) {
    issues.push({ section: 'post-urls', reason: 'invalid-row' });
  }

  return issues;
}
